/// <reference types="vite-plugin-pwa/client" />
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { anunciarAtualizacao } from './lib/atualizacao.js';
import App from './App.js';

/**
 * Fontes da spec 7.2, auto-hospedadas.
 *
 * Via `@fontsource` (arquivos servidos do proprio bundle) e nao do Google
 * Fonts: a CSP da spec 6.5 fecha em `default-src 'self'`, entao um `<link>`
 * para fonts.googleapis.com seria bloqueado em producao — e o app cairia em
 * `system-ui` sem nenhum aviso.
 *
 * Somente os pesos efetivamente usados, e somente o subset `latin`. O import
 * sem subset traz cirilico, grego e vietnamita junto: o browser nao baixa o
 * que nao usa (o `unicode-range` cuida disso), mas os arquivos vao para o
 * `dist` de qualquer forma. O latin cobre todo o pt-BR — os acentos da lingua
 * (a c e i o u com til, cedilha e agudo) vivem em U+0000–00FF.
 *
 * Cada peso extra e um arquivo a mais no caminho critico, e a meta e bundle
 * inicial < 200 kB gzip (spec 11).
 */
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter-tight/latin-600.css';
import '@fontsource/inter-tight/latin-700.css';
// Apenas o digito do Pomodoro e os contadores de dias (spec 7.2).
import '@fontsource/jetbrains-mono/latin-500.css';

import './styles/index.css';

/**
 * Service worker (spec 11).
 *
 * Registrado ANTES do render, e nao dentro de um efeito: o cache do app shell
 * precisa comecar a se formar na primeira visita, que e a unica em que o
 * usuario esta garantidamente online.
 *
 * A atualizacao e OFERECIDA, nao imposta (`registerType: 'prompt'`): recarregar
 * sozinho por causa de um deploy descartaria o que a pessoa estivesse
 * digitando.
 *
 * **`onNeedRefresh` nao existia, e por isso a oferta nunca era feita.** O
 * comentario anterior afirmava que "a versao nova entra na proxima abertura,
 * comportamento padrao do navegador" — e isso e falso: um service worker em
 * espera so assume quando TODOS os clientes da origem se fecham, o que num PWA
 * instalado na tela de inicio pode nao acontecer nunca. Na pratica o deploy
 * ficava no ar e o aparelho continuava servindo o bundle antigo do cache.
 */
const atualizar = registerSW({
  immediate: true,

  onNeedRefresh() {
    // Nao recarrega aqui: so acende o aviso. Quem decide e a pessoa, no botao
    // de `AtualizacaoDisponivel`.
    anunciarAtualizacao(() => void atualizar(true));
  },

  onRegisteredSW(_url, registro) {
    if (!registro) return;

    /*
      Uma aba aberta o dia inteiro so procura versao nova no carregamento.
      Sem estas duas checagens, quem deixa o app aberto — o caso comum de quem
      trabalha com ele do lado — nunca veria o aviso.

      `visibilitychange` cobre o celular, onde o sistema congela abas ocultas e
      o intervalo nao roda; a hora cobre o desktop deixado aberto.
    */
    const procurar = () => void registro.update();

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') procurar();
    });

    setInterval(procurar, 60 * 60 * 1000);
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('elemento #root nao encontrado em index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
